import {
  LineChart as RLineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { CHART_COLORS } from '../../config/constants';
import { useChartTheme } from '../../hooks/useChartTheme';
import { formatCompact } from '../../utils/formatters';

// Rich tooltip: colored dots, biggest value first, right-aligned numbers.
function ChartTooltip({ active, payload, label, ct, note, labelFormatter }) {
  if (!active || !payload || !payload.length) return null;
  const rows = payload.filter((p) => p.value != null).sort((a, b) => b.value - a.value);
  if (!rows.length) return null;
  return (
    <div style={{ ...ct.tooltip, padding: '9px 11px', minWidth: 150 }}>
      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: note ? 2 : 7, color: ct.tooltip.color }}>{labelFormatter ? labelFormatter(label) : label}</div>
      {note && <div style={{ fontSize: 10, opacity: 0.55, marginBottom: 7, color: ct.tooltip.color }}>{note}</div>}
      <div style={{ display: 'grid', rowGap: 4 }}>
        {rows.map((p) => (
          <div key={p.dataKey} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: ct.tooltip.color }}>
            <span style={{ width: 9, height: 9, borderRadius: 3, background: p.color, flexShrink: 0 }} />
            <span style={{ flex: 1, opacity: 0.8, whiteSpace: 'nowrap' }}>{p.dataKey}</span>
            <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{Number(p.value).toLocaleString()}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// One line per series (e.g. per product) over a shared X axis.
//   data:        [{ period, [seriesName]: value, ... }]  (pivoted, one key per series)
//   series:      ordered array of series names to draw
//   colorFor:    optional name -> color map, so a series keeps its color regardless of
//                which others are selected. Falls back to CHART_COLORS by index.
//   activeSeries: when set, that line is emphasized and the rest are dimmed (hover focus).
//   xTickFormatter: optionally shortens axis tick labels (tooltip keeps the raw value).
export function MultiLineChart({ data, series = [], colorFor, activeSeries, xKey = 'period', height = 300, xTickFormatter, labelFormatter, note }) {
  const ct = useChartTheme();
  const color = (name, i) => (colorFor && colorFor[name]) || CHART_COLORS[i % CHART_COLORS.length];
  return (
    <ResponsiveContainer width="100%" height={height}>
      <RLineChart data={data} margin={{ top: 10, right: 16, bottom: 8, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={ct.grid} vertical={false} />
        <XAxis
          dataKey={xKey}
          tickFormatter={xTickFormatter}
          tick={{ fontSize: 12, fill: ct.tick }}
          axisLine={{ stroke: ct.axis }}
          tickLine={false}
          padding={{ left: 8, right: 8 }}
          interval="preserveStartEnd"
          minTickGap={44}
        />
        <YAxis tick={{ fontSize: 12, fill: ct.tick }} axisLine={false} tickLine={false} width={48} tickFormatter={formatCompact} />
        <Tooltip
          cursor={{ stroke: ct.axis, strokeWidth: 1, strokeDasharray: '4 4' }}
          content={(props) => <ChartTooltip {...props} ct={ct} note={note} labelFormatter={labelFormatter} />}
        />
        {series.map((name, i) => {
          const c = color(name, i);
          const dimmed = activeSeries && activeSeries !== name;
          const focused = activeSeries === name;
          return (
            <Line
              key={name}
              type="monotone"
              dataKey={name}
              stroke={c}
              strokeWidth={focused ? 3.5 : 2.4}
              strokeOpacity={dimmed ? 0.12 : 1}
              strokeLinecap="round"
              dot={false}
              activeDot={{ r: 5, strokeWidth: 2, stroke: ct.tooltip.backgroundColor }}
              connectNulls
              animationDuration={600}
              isAnimationActive
            />
          );
        })}
      </RLineChart>
    </ResponsiveContainer>
  );
}
